<template>
	<Panel
		title="Vehicle Registration and Titling Information"
		class="PanelVehicleRegistration"
	>
		<div :style="$grid('1-1')">
			<InputRichDropdown
				label="Vehicle Delivery Method"
				v-model:saturn="registrationInfo.deliveryMethod"
				:list="getDeliveryMethodsEnumHelper.enumList"
				:valueMap="getDeliveryMethodsEnumHelper.getToListItemValue"
				:display="getDeliveryMethodsEnumHelper.getToListItemDisplay"
				nothingSelected="-- Select Value --"
				:invalid="v.deliveryMethod.$invalid"
			>
				<template #validation>
					<div v-if="v.deliveryMethod.required.$invalid">Delivery Method is required</div>
				</template>
			</InputRichDropdown>
			<InputRichDropdown
				label="Primary Use"
				v-model:saturn="registrationInfo.primaryUsage"
				:list="vehiclePrimaryUsageList"
				:valueMap="vehiclePrimaryUsageEnumHelper.getToListItemValue"
				:display="vehiclePrimaryUsageEnumHelper.getToListItemDisplay"
				:invalid="v.primaryUsage.$invalid"
				:sortMode="DropdownSortMode.Custom"
				:sortCompare="sortPrimaryUse"
				search
				nothingSelected="-- Select Value --"
			>
				<template #validation>
					<div v-if="v.primaryUsage.required.$invalid">Primary usage is required</div>
				</template>
			</InputRichDropdown>
		</div>

		<div v-if="registrationInfo.primaryUsage == VehiclePrimaryUsage.EXPORT">
			<PanelExportInfo
				:registrationInfo="registrationInfo"
				:fallbackzipcode="fimenu.store.storeZip"
				:validation="validation.vehicle.registrationInfo"
			>
			</PanelExportInfo>
		</div>

		<div :style="$grid('1-1')">
			<div
				:style="$grid('7-4')"
				class="no-margin-grid"
			>
				<div
					class="radio-button-inline"
					:invalid="v.isPlateTransfer.$invalid"
				>
					<span>Is the plate being transfered?</span>
					<InputRadio
						label="Yes"
						:valueMatch="true"
						v-model="registrationInfo.isPlateTransfer"
						:isButton="true"
					/>
					<InputRadio
						label="No"
						:valueMatch="false"
						v-model="registrationInfo.isPlateTransfer"
						:isButton="true"
					/>
				</div>
				<SelectVehicle
					:fimenu="fimenu"
					label="Load Plate From Trade-In"
					ref="vehicleSelector"
					:disabled="!registrationInfo.isPlateTransfer"
				/>
			</div>

			<div
				:style="$grid('1-1')"
				v-if="plateTransactionsEnabled && registrationInfo.isPlateTransfer"
				class="no-margin-grid"
			>
				<button
					@click="handleOpenDDiResponseModal"
					:disabled="loadingVehicleInquiryModal || v.plateNumber.$invalid"
					class="ignore-all-locks"
				>
					<i
						v-if="loadingVehicleInquiryModal"
						class="fas fa-spinner fa-spin"
					/>
					<span v-else>View Vehicle Inquiry</span>
				</button>
			</div>
		</div>

		<div :style="$grid('1')">
			<fieldset
				:style="$grid('1-1-1-1')"
				class="no-margin-grid"
				:disabled="!registrationInfo.isPlateTransfer"
			>
				<InputTextbox
					label="Plate Number"
					v-model:saturn="registrationInfo.plateNumber"
					:invalid="v.plateNumber.$invalid"
					placeholder="Plate Number"
				>
					<template #validation>
						<div v-if="v.plateNumber.required.$invalid">Plate Number is required</div>
					</template>
				</InputTextbox>

				<InputRichDropdown
					label="Plate State"
					v-model:saturn="registrationInfo.plateState"
					:list="stateHelper.enumList"
					:invalid="v.plateState.$invalid"
					:valueMap="stateHelper.getToListItemValue"
					:display="stateHelper.getStateDescription"
					search
				>
					<template #validation>
						<div v-if="v.plateState.required.$invalid">Plate State is required</div>
					</template>
				</InputRichDropdown>

				<InputDate
					label="Current Plate Expiration Date"
					v-model:saturn="registrationInfo.currentPlateExpirationDate"
					:invalid="v.currentPlateExpirationDate.$invalid"
					icon="fas fa-calendar"
				>
					<template #validation>
						<div v-if="v.currentPlateExpirationDate.required.$invalid">Current Plate Expiration Date is Required</div>
					</template>
				</InputDate>

				<InputNumber
					label="Registration Duration (months)"
					v-model:saturn="registrationInfo.registrationLengthInMonths"
					:invalid="v.registrationLengthInMonths.$invalid"
					placeholder="Registration Length In Months"
					:precision="0"
				>
					<template #validation>
						<div v-if="v.registrationLengthInMonths.required.$invalid">Registration Length is required</div>
					</template>
				</InputNumber>
			</fieldset>
		</div>

		<PanelDealAddresses
			:fimenu="fimenu"
			:validation="validation"
			:disabled="fimenu.isDealLocked()"
			class="address-container"
		/>

		<div
			class="radio-button-inline"
			:invalid="util.isNull(validation) ? false : v.titledUnder.$invalid"
			:style="$grid('411px-1')"
		>
			<span>Who is the vehicle being titled under?</span>
			<InputRichDropdown
				v-model:saturn="registrationInfo.titledUnder"
				:list="vehicleTitledUnderEnumHelper.enumList"
				:invalid="v.titledUnder.$invalid"
				:valueMap="vehicleTitledUnderEnumHelper.getToListItemValue"
				:display="vehicleTitledUnderEnumHelper.getToListItemDisplay"
				:disabled="!fimenu.hasCoSigner"
			/>
		</div>
	</Panel>
</template>

<script setup lang="ts">
	import {
		GetDeliveryMethodsEnumHelper,
		IsPlateTransactionsEnabled,
		TransferTypesList,
		VehiclePrimaryUsageEnumHelper,
		VehicleTitledUnderEnumHelper,
	} from '@/helpers/plate-transactions-helper';
	import { ManualSubmitCreditApplication } from '@core/helpers/finance-channels-helper';
	import { openDDiResponseModal } from '@/helpers/plate-transactions-helper';

	import { ref, computed, onMounted, watch, inject } from 'vue';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';
	import { useDealProcessesRunningStore } from '@core/stores/DealProcessesRunningStore';

	import { VehiclePrimaryUsage, VehicleTitledUnder, DropdownSortMode, EmployeePermissionsType } from '@core/classes/SharedEnums';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuVehicleRegistrationInfo from '@core/classes/FIMenuVehicleRegistrationInfo';

	import InputDate from '@core/components/InputDate.vue';
	import InputNumber from '@core/components/InputNumber.vue';
	import InputRadio from '@core/components/InputRadio.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import LogRocket from 'logrocket';
	import Panel from '@core/components/Panel.vue';
	import PanelDealAddresses from '@/components/PanelDealAddresses.vue';
	import PanelExportInfo from '@/components/PanelExportInfo.vue';
	import SelectVehicle from '@/components/SelectVehicle.vue';

	import StateHelper from '@core/classes/StateHelper';
	import TitleStatusEnumsHelper from '@core/_enums/TitleStatusEnumHelper';
	import util from '@core/services/util';
	import VersionsLender from '@core/classes/LenderVersionClass';

	const props = defineProps<{
		fimenu: FIMenu;
		validation: any;
	}>();
	const dealProcessesRunningStore = useDealProcessesRunningStore();

	const $global = inject<SaturnGlobalPlugin>('$global');

	const vehicleSelector = ref();
	const loadingVehicleInquiryModal = ref(false);

	// Initialize helpers
	const stateHelper = new StateHelper();
	const vehiclePrimaryUsageEnumHelper = new VehiclePrimaryUsageEnumHelper();
	const vehicleTitledUnderEnumHelper = new VehicleTitledUnderEnumHelper();
	const titleStatusEnumsHelper = new TitleStatusEnumsHelper();
	const getDeliveryMethodsEnumHelper = new GetDeliveryMethodsEnumHelper();
	const transferTypesList = new TransferTypesList();

	// Initialize registration info
	const registrationInfo = ref(props.fimenu.vehicle.registrationInfo as FIMenuVehicleRegistrationInfo);

	// Computed properties
	const v = computed(() => props.validation.vehicle.registrationInfo);
	const plateTransactionsEnabled = computed(() => IsPlateTransactionsEnabled(props.fimenu));

	const vehiclePrimaryUsageList = computed(() => {
		// Define sort weights for different scenarios
		const SORT_WEIGHTS = {
			FIRST_PRIORITY: -1,
			SECOND_PRIORITY: 0,
			DEFAULT: 1,
		};

		return vehiclePrimaryUsageEnumHelper.enumList.map(item => {
			// Start with default sort weight
			let sortWeight = SORT_WEIGHTS.DEFAULT;

			// For non-entity customers (individuals)
			if (!props.fimenu.customer.isEntity) {
				if (item.value === VehiclePrimaryUsage.PERSONAL) {
					sortWeight = SORT_WEIGHTS.FIRST_PRIORITY; // Personal use first
				} else if (item.value === VehiclePrimaryUsage.BUSINESS) {
					sortWeight = SORT_WEIGHTS.SECOND_PRIORITY; // Business use second
				}
			}
			// For entity customers (businesses)
			else {
				if (item.value === VehiclePrimaryUsage.BUSINESS) {
					sortWeight = SORT_WEIGHTS.FIRST_PRIORITY; // Business use first
				} else if (item.value === VehiclePrimaryUsage.PERSONAL) {
					sortWeight = SORT_WEIGHTS.SECOND_PRIORITY; // Personal use second
				}
			}

			return {
				...item,
				sortWeight,
			};
		});
	});

	// Sort function that uses the pre-calculated sort weights
	const sortPrimaryUse = (a: any, b: any) => {
		return a.obj.sortWeight - b.obj.sortWeight;
	};

	// Methods
	const resetRegistrationInfo = () => {
		registrationInfo.value.plateNumber = null;
		registrationInfo.value.plateState = null;
		registrationInfo.value.currentPlateExpirationDate = null;
		registrationInfo.value.registrationLengthInMonths = null;
	};

	const handleOpenDDiResponseModal = async () => {
		loadingVehicleInquiryModal.value = true;
		await openDDiResponseModal(props.fimenu.storeCode, null, props.fimenu.id, registrationInfo.value.plateNumber, registrationInfo.value.plateState);
		loadingVehicleInquiryModal.value = false;
	};

	const handleTitleStatusChange = async () => {
		await props.fimenu.save();
	};

	const triggerCreditApplication = async () => {
		try {
			if (props.fimenu.vehicle.registrationInfo.primaryUsage === VehiclePrimaryUsage.BUSINESS) throw new Error('Primary usage -Business');
			if (props.fimenu.customer.isEntity || props.fimenu.coCustomer?.isEntity) throw new Error('Customer - Business');
			if (!props.fimenu.store.storeSettings.softPullAllyProgram) {
				return LogRocket.log('Soft pull ally program not enabled');
			}

			if (dealProcessesRunningStore.CreditApplicationProcesses.isAutoSubmitToAlly) {
				return LogRocket.log('Credit application already being submitted');
			}
			dealProcessesRunningStore.setSubmittingCreditApplication(true);

			// Only proceed if credit applications are enabled for this store
			if (!props.fimenu.creditApplicationsEnabled) return LogRocket.log('Credit applications not enabled');
			// Only proceed if buyer's order is enabled for this store
			if (!props.fimenu.buyersOrderEnabled) return LogRocket.log('Buyers order not enabled');
			// Skip if credit applications have already been submitted
			if (!!props.fimenu.dealJacket.applications?.length && props.fimenu.dealJacket.applications?.length > 0)
				return LogRocket.log('Credit applications already submitted');
			// Skip if paperwork packets already exist
			if (props.fimenu.paperwork.packets?.length > 0) return LogRocket.log('Paperwork packets already exist');
			// Skip if there's a current paperwork packet in progress
			if (props.fimenu.paperwork.currentPacket() != null) return LogRocket.log('Current paperwork packet in progress');
			// Find the Ally Financial lender in the global lenders list
			const lender = $global.Lenders.find((l: VersionsLender) => l.lenderCode === 'ALLY');
			// Only proceed if Ally lender is found
			if (!lender) return LogRocket.log('Ally lender not found');

			// Submit the credit application to Ally
			await ManualSubmitCreditApplication(props.fimenu, lender);
		} catch (err) {
			console.log('Error', err);
		} finally {
			dealProcessesRunningStore.setSubmittingCreditApplication(false);
		}
	};

	// Lifecycle hooks
	onMounted(() => {
		watch(
			() => registrationInfo.value.isPlateTransfer,
			newVal => {
				if (newVal === false) {
					resetRegistrationInfo();
				}
			},
			{ immediate: true },
		);

		if (registrationInfo.value.titledUnder === null && !props.fimenu.hasCoSigner) {
			registrationInfo.value.titledUnder = VehicleTitledUnder.Customer;
		}
		//This has it's own check inside the function to see if it should trigger or not.
		triggerCreditApplication();
	});
</script>

<style>
	.PanelVehicleRegistration > .panel-body {
		display: flex;
		flex-direction: column;
		gap: 10px;
		height: fit-content;
		padding-bottom: 30px;
	}

	.PanelVehicleRegistration .two-sided {
		grid-template-columns: 1fr 1fr;
	}

	.PanelVehicleRegistration .questions-container {
		grid-template-columns: 1fr 1fr 1fr;
		height: auto;
	}

	.PanelVehicleRegistration .two-sided > *,
	.PanelVehicleRegistration .questions-container > * {
		width: 100%;
	}

	.PanelVehicleRegistration .radio-button-inline {
		min-height: 40px;
	}
</style>
